import { Tooltip } from '@mui/material';
import type { CSSProperties, ReactElement } from 'react';
import { isBooleanValueTrue } from '../utils/booleanFieldUtils';
import type { GridCellTemplateRenderer } from './types';

const TRACKER_COLOR = '#55de52';
const MANUAL_COLOR = '#007bff';
const REMAINING_COLOR = '#d3d3d3';
const BOOLEAN_TRUE_COLOR = '#f38e22';
const BASE_COLOR = '#f0f0f0';
const SECONDS_IN_HOUR = 3600;

type ProgressBarInfo = {
  WorkingDayHours: number;
  TrackedWithTrackerTime: number;
  TrackedManuallyTime: number;
  Overtime: number;
};

const handledEmptyResult = { content: null };

const formatHours = (decimalHours: number): string => {
  const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sign = decimalHours < 0 && totalMinutes > 0 ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

const readOptionalProgressValue = (
  source: Record<string, unknown>,
  key: keyof Omit<ProgressBarInfo, 'WorkingDayHours'>
): number | null => {
  const value = source[key] ?? 0;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
};

const parseProgressBarInfo = (value: unknown): ProgressBarInfo | null => {
  if (!value || typeof value !== 'string') return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const source = parsed as Record<string, unknown>;
    const total = source.WorkingDayHours;
    if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return null;

    const tracker = readOptionalProgressValue(source, 'TrackedWithTrackerTime');
    const manual = readOptionalProgressValue(source, 'TrackedManuallyTime');
    const overtime = readOptionalProgressValue(source, 'Overtime');
    if (tracker === null || manual === null || overtime === null) return null;

    return {
      WorkingDayHours: total,
      TrackedWithTrackerTime: tracker,
      TrackedManuallyTime: manual,
      Overtime: overtime,
    };
  } catch {
    return null;
  }
};

const wrapperStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '18px',
};

const segment = (color: string, width: string, name: string): ReactElement => (
  <div
    data-segment={name}
    style={{
      backgroundColor: color,
      width,
      height: '100%',
    }}
  />
);

export const decorateBooleanBar: GridCellTemplateRenderer = ({ value }) => ({
  content: (
    <div data-cell-template="decorateBooleanBar" style={wrapperStyle}>
      <div
        data-boolean-bar
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: isBooleanValueTrue(value) ? BOOLEAN_TRUE_COLOR : REMAINING_COLOR,
          borderRadius: '4px',
        }}
      />
    </div>
  ),
});

export const decorateTimeProgressBar: GridCellTemplateRenderer = ({ value }) => {
  const data = parseProgressBarInfo(value);
  if (!data) return handledEmptyResult;

  const total = data.WorkingDayHours;
  const tracker = data.TrackedWithTrackerTime;
  const manual = data.TrackedManuallyTime;
  const remaining = Math.max(0, total - (tracker + manual));
  const toPercent = (part: number) => `${((part / total) * 100).toFixed(2)}%`;

  const tooltip = (
    <div
      data-progress-tooltip
      style={{ display: 'flex', flexDirection: 'column', alignContent: 'flex-start' }}
    >
      <p style={{ padding: 0, margin: 0, width: '100%', textAlign: 'left' }}>
        <strong>Tracked:</strong> {formatHours(tracker / SECONDS_IN_HOUR)} hours
      </p>
      <p style={{ padding: 0, margin: 0, width: '100%', textAlign: 'left' }}>
        <strong>Manually:</strong> {formatHours(manual / SECONDS_IN_HOUR)} hours
      </p>
      <p style={{ padding: 0, margin: 0, width: '100%', textAlign: 'left' }}>
        <strong>Over time:</strong> {formatHours(data.Overtime / SECONDS_IN_HOUR)} hours
      </p>
    </div>
  );

  return {
    content: (
      <Tooltip title={tooltip}>
        <div
          data-cell-template="decorateTimeProgressBar"
          style={{ ...wrapperStyle, cursor: 'pointer' }}
        >
          <div
            data-progress-bar
            style={{
              height: '8px',
              width: '100%',
              display: 'flex',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: BASE_COLOR,
            }}
          >
            {segment(TRACKER_COLOR, toPercent(tracker), 'tracker')}
            {segment(MANUAL_COLOR, toPercent(manual), 'manual')}
            {segment(REMAINING_COLOR, toPercent(remaining), 'remaining')}
          </div>
        </div>
      </Tooltip>
    ),
  };
};

export const customizeDurationDisplay: GridCellTemplateRenderer = ({ value }) => {
  if (
    (typeof value !== 'number' && typeof value !== 'string') ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return handledEmptyResult;
  }

  const decimalHours = Number(value);
  if (!Number.isFinite(decimalHours)) return handledEmptyResult;

  const [hours, minutes] = formatHours(decimalHours).split(':');
  return {
    content: (
      <span data-cell-template="customizeDurationDisplay">
        {hours} hrs {Number.parseInt(minutes, 10)} min
      </span>
    ),
  };
};
